import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ErrorState, LoadingBar } from '@web/components/Feedback';
import { useSession } from '@web/hooks/useSession';
import { startRealtime } from '@web/lib/realtime';
import { setConnection, socket } from '@web/lib/socket';

/**
 * The session gate.
 *
 * `isPending` renders a bar rather than the login screen: flashing "sign in" at
 * someone who is already signed in, every time they open the app, teaches them
 * to distrust it.
 *
 * The socket is connected HERE and nowhere else. Connecting before the session
 * is known just gets refused at the handshake and starts a reconnect loop
 * behind the login form.
 */
export function Protected({ role }: { role?: 'admin' } = {}) {
  const session = useSession();
  const location = useLocation();
  const client = useQueryClient();

  const authenticated = session.data != null;

  useEffect(() => {
    if (!authenticated) return;

    setConnection('connecting');
    if (socket.disconnected) socket.connect();
    const stop = startRealtime(client);

    return () => {
      stop();
    };
  }, [authenticated, client]);

  useEffect(() => {
    if (authenticated) return;
    socket.disconnect();
    setConnection('idle');
  }, [authenticated]);

  if (session.isPending) return <LoadingBar label="Checking your session" />;

  if (session.error !== null) {
    return (
      <ErrorState
        error={session.error}
        onRetry={() => {
          void session.refetch();
        }}
      />
    );
  }

  if (!authenticated) {
    return <Navigate to={`/login?from=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // A client-side role check is cosmetic — the server returns 403 regardless —
  // but showing an agent an admin screen they cannot use is its own bug.
  if (role === 'admin' && session.data?.role !== 'admin') {
    return <Navigate to="/board" replace />;
  }

  return <Outlet />;
}
